import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Users, Gift, Plus, Edit, Trash2, Search, Filter, Calendar, DollarSign, Trophy, Eye, Settings, Ticket, Check, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import toast, { Toaster } from 'react-hot-toast'
import Header from './Header'
import Footer from './Footer'
import { statsService, userService } from '../services/api'
import { membresiaService } from '../services/sorteoService'
import { useRequireAuth } from '../hooks/useAuth'

const AdminPage = () => {
  const { user, loading: authLoading } = useRequireAuth('admin')
  const [activeTab, setActiveTab] = useState('dashboard')
  const [searchTerm, setSearchTerm] = useState('')
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeRaffles: 0,
    totalRevenue: 0,
    ticketsSold: 0
  })
  const [recentActivity, setRecentActivity] = useState([])
  const [users, setUsers] = useState([])
  const [sorteos, setSorteos] = useState([])
  const [loading, setLoading] = useState(true)
  const [usersLoading, setUsersLoading] = useState(true)
  const [sorteosLoading, setSorteosLoading] = useState(true)
  const [selectedUser, setSelectedUser] = useState(null)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  
  // Estados para sorteos
  const [selectedSorteo, setSelectedSorteo] = useState(null)
  const [showEditSorteoModal, setShowEditSorteoModal] = useState(false)
  const [showDeleteSorteoModal, setShowDeleteSorteoModal] = useState(false)
  
  // Estados para vista de boletos comprados
  const [showPurchasedTickets, setShowPurchasedTickets] = useState(false)
  const [purchasedTicketsData, setPurchasedTicketsData] = useState([])
  const [purchasedTicketsLoading, setPurchasedTicketsLoading] = useState(false)
  const [selectedSorteoForTickets, setSelectedSorteoForTickets] = useState(null)
  const [statusFilter, setStatusFilter] = useState('all') // 'all', 'pending', 'paid'
  const [showApproveModal, setShowApproveModal] = useState(false)
  const [showDeleteTransactionModal, setShowDeleteTransactionModal] = useState(false)
  const [selectedTransaction, setSelectedTransaction] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)
  
  // Estados para creación por pasos
  const [createStep, setCreateStep] = useState(1) // 1: Datos, 2: Premios, 3: Resumen
  const [createdSorteoId, setCreatedSorteoId] = useState(null)
  const [createSorteoForm, setCreateSorteoForm] = useState({
    name: '',
    description: '',
    amount: '',
    total_boletas: '',
    start_date: '',
    end_date: '',
    image_url: ''
  })
  const [premiosSecundarios, setPremiosSecundarios] = useState([])
  const [imageFile, setImageFile] = useState(null)
  const [createLoading, setCreateLoading] = useState(false)
  
  const navigate = useNavigate()

  // Cargar datos del dashboard
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true)
        const [statsData, activityData] = await Promise.all([
          statsService.getDashboardStats(),
          statsService.getRecentActivity()
        ])
        setStats(statsData)
        setRecentActivity(activityData)
      } catch (error) {
        console.error('Error al cargar datos del dashboard:', error)
      } finally {
        setLoading(false)
      }
    }

    loadDashboardData()
  }, [])

  // Cargar usuarios reales (solo clientes)
  useEffect(() => {
    const loadUsers = async () => {
      try {
        setUsersLoading(true)
        const response = await userService.getAllUsers()
        // Filtrar solo usuarios con role 'client'
        const clients = (response.users || []).filter(user => user.role === 'client')
        setUsers(clients)
      } catch (error) {
        console.error('Error al cargar usuarios:', error)
      } finally {
        setUsersLoading(false)
      }
    }

    loadUsers()
  }, [])

  // Cargar sorteos reales
  useEffect(() => {
    const loadSorteos = async () => {
      try {
        setSorteosLoading(true)
        const response = await membresiaService.getAllMembresias()
        setSorteos(response || [])
      } catch (error) {
        console.error('Error al cargar sorteos:', error)
      } finally {
        setSorteosLoading(false)
      }
    }

    loadSorteos()
  }, [])

  // Mostrar loading mientras se verifica la autenticación
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Verificando permisos...</p>
        </div>
      </div>
    )
  }

  // Funciones para manejar modales
  const handleViewUser = (user) => {
    setSelectedUser(user)
    setShowViewModal(true)
  }

  const handleEditUser = (user) => {
    setSelectedUser(user)
    setShowEditModal(true)
  }

  const handleDeleteUser = (user) => {
    setSelectedUser(user)
    setShowDeleteModal(true)
  }

  const closeModals = () => {
    setShowViewModal(false)
    setShowEditModal(false)
    setShowDeleteModal(false)
    setSelectedUser(null)
  }

  // Funciones para manejar modales de sorteos
  const handleViewSorteo = (sorteo) => {
    navigate(`/membresia/${sorteo.id}`)
  }

  const handleEditSorteo = (sorteo) => {
    setSelectedSorteo(sorteo)
    setShowEditSorteoModal(true)
  }

  const handleDeleteSorteo = (sorteo) => {
    setSelectedSorteo(sorteo)
    setShowDeleteSorteoModal(true)
  }

  const loadPurchasedTickets = async (sorteoId, status = 'all') => {
    setPurchasedTicketsLoading(true)
    
    try {
      let url = `/api/transactions/admin/sorteo/${sorteoId}/purchased-tickets`
      if (status !== 'all') {
        url += `?status=${status}`
      }
      
      const token = localStorage.getItem('authToken')
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      const data = await response.json()
      
      if (response.ok) {
        setPurchasedTicketsData(data.tickets || [])
      } else {
        console.error('Error al obtener boletos:', data.error || data.message || 'Error desconocido')
        setPurchasedTicketsData([])
      }
    } catch (error) {
      console.error('Error al cargar boletos:', error)
      setPurchasedTicketsData([])
    } finally {
      setPurchasedTicketsLoading(false)
    }
  }

  const handleViewPurchasedTickets = async (sorteo) => {
    setSelectedSorteoForTickets(sorteo)
    setShowPurchasedTickets(true)
    setStatusFilter('all')
    await loadPurchasedTickets(sorteo.id, 'all')
  }

  const handleStatusFilterChange = async (newStatus) => {
    setStatusFilter(newStatus)
    if (selectedSorteoForTickets) {
      await loadPurchasedTickets(selectedSorteoForTickets.id, newStatus)
    }
  }

  const handleApproveTransaction = (reference) => {
    setSelectedTransaction(reference)
    setShowApproveModal(true)
  }

  const handleDeleteTransaction = (reference) => {
    setSelectedTransaction(reference)
    setShowDeleteTransactionModal(true)
  }

  const confirmApproveTransaction = async () => {
    if (!selectedTransaction) return
    
    setActionLoading(true)
    try {
      const response = await fetch(`/api/transactions/admin/approve/${selectedTransaction}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      })
      
      const data = await response.json()
      
      if (response.ok) {
        toast.success('Transacción aprobada exitosamente')
        // Recargar los datos
        await loadPurchasedTickets(selectedSorteoForTickets.id, statusFilter)
      } else {
        toast.error(data.error || 'Error al aprobar transacción')
      }
    } catch (error) {
      console.error('Error al aprobar transacción:', error)
      toast.error('Error al aprobar transacción')
    } finally {
      setActionLoading(false)
      setShowApproveModal(false)
      setSelectedTransaction(null)
    }
  }

  const confirmDeleteTransaction = async () => {
    if (!selectedTransaction) return
    
    setActionLoading(true)
    try {
      const response = await fetch(`/api/transactions/admin/delete/${selectedTransaction}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      })
      
      const data = await response.json()
      
      if (response.ok) {
        toast.success(`Transacción eliminada. ${data.freed_numbers?.length || 0} boletos liberados`)
        // Recargar los datos
        await loadPurchasedTickets(selectedSorteoForTickets.id, statusFilter)
      } else {
        toast.error(data.error || 'Error al eliminar transacción')
      }
    } catch (error) {
      console.error('Error al eliminar transacción:', error)
      toast.error('Error al eliminar transacción')
    } finally {
      setActionLoading(false)
      setShowDeleteTransactionModal(false)
      setSelectedTransaction(null)
    }
  }

  const closeSorteoModals = () => {
    setShowEditSorteoModal(false)
    setShowDeleteSorteoModal(false)
    setSelectedSorteo(null)
  }
  
  const closePurchasedTicketsView = () => {
    setShowPurchasedTickets(false)
    setSelectedSorteoForTickets(null)
    setPurchasedTicketsData([])
    setStatusFilter('all')
  }

  // Función para crear sorteo básico (Paso 1)
  const handleCreateSorteoBasico = async (e) => {
    e.preventDefault()
    setCreateLoading(true)
    
    try {
      let imageUrl = createSorteoForm.image_url
      
      // Si hay un archivo de imagen, subirlo primero
      if (imageFile) {
        try {
          const uploadResponse = await membresiaService.uploadImage(imageFile)
          imageUrl = uploadResponse.imageUrl
        } catch (uploadError) {
          console.error('Error al subir imagen:', uploadError)
          toast.error('Error al subir la imagen')
          return
        }
      }
      
      const sorteoData = {
        ...createSorteoForm,
        amount: parseFloat(createSorteoForm.amount),
        total_boletas: parseInt(createSorteoForm.total_boletas),
        image_url: imageUrl
      }
      
      const response = await membresiaService.createMembresia(sorteoData)
      setCreatedSorteoId(response.id)
      
      // Pasar al siguiente paso
      setCreateStep(2)
      
      toast.success('Datos del sorteo guardados. Ahora agrega los premios secundarios.')
    } catch (error) {
      console.error('Error al crear sorteo:', error)
      toast.error('Error al crear sorteo')
    } finally {
      setCreateLoading(false)
    }
  }
  
  // Función para finalizar creación (Paso 3)
  const handleFinalizarSorteo = async () => {
    try {
      // Recargar lista de sorteos
      const response = await membresiaService.getAllMembresias()
      setSorteos(response || [])
      
      // Limpiar estados
      resetCreateForm()
      
      // Volver a la pestaña de sorteos
      setActiveTab('raffles')
      
      toast.success('Sorteo creado exitosamente con todos sus premios')
    } catch (error) {
      console.error('Error al finalizar sorteo:', error)
      toast.error('Error al finalizar sorteo')
    }
  }
  
  // Función para resetear formulario
  const resetCreateForm = () => {
    setCreateStep(1)
    setCreatedSorteoId(null)
    setCreateSorteoForm({
      name: '',
      description: '',
      amount: '',
      total_boletas: '',
      start_date: '',
      end_date: '',
      image_url: ''
    })
    setPremiosSecundarios([])
    setImageFile(null)
  }

  // Función para agregar premio secundario
  const addPremioSecundario = () => {
    const newPremio = {
      id: Date.now(),
      numero_boletos: '',
      premio: '',
      numeros_seleccionados: ''
    }
    setPremiosSecundarios([...premiosSecundarios, newPremio])
  }

  // Función para eliminar premio secundario
  const removePremioSecundario = (id) => {
    setPremiosSecundarios(premiosSecundarios.filter(p => p.id !== id))
  }

  // Función para actualizar premio secundario
  const updatePremioSecundario = (id, field, value) => {
    setPremiosSecundarios(premiosSecundarios.map(p => 
      p.id === id ? { ...p, [field]: value } : p
    ))
  }
  
  // Función para guardar premio secundario individual
  const guardarPremioSecundario = async (premio) => {
    if (!createdSorteoId) {
      toast.error('Error: No hay sorteo creado')
      return
    }
    
    if (!premio.numero_boletos || !premio.premio || !premio.numeros_seleccionados) {
      toast.error('Completa todos los campos del premio')
      return
    }
    
    try {
      const premioData = {
        sorteo_id: createdSorteoId,
        numero_boletos: parseInt(premio.numero_boletos),
        premio: premio.premio,
        numeros_seleccionados: premio.numeros_seleccionados
      }
      
      await membresiaService.createPremioSecundario(premioData)
      toast.success('Premio secundario guardado exitosamente')
      
      // Marcar como guardado
      updatePremioSecundario(premio.id, 'guardado', true)
    } catch (error) {
      console.error('Error al guardar premio secundario:', error)
      toast.error('Error al guardar premio secundario')
    }
  }

  // Función para generar números automáticamente
  const generarNumerosAutomaticos = (premioId) => {
    const premio = premiosSecundarios.find(p => p.id === premioId)
    const cantidadBoletos = parseInt(premio.numero_boletos)
    
    if (!cantidadBoletos || cantidadBoletos <= 0) {
      toast.error('Ingresa primero la cantidad de boletos')
      return
    }
    
    const totalBoletas = parseInt(createSorteoForm.total_boletas) || 100000
    
    // Obtener todos los números ya utilizados en otros premios
    const numerosYaUtilizados = new Set()
    premiosSecundarios.forEach(p => {
      if (p.id !== premioId && p.numeros_seleccionados) {
        const numeros = p.numeros_seleccionados.split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n))
        numeros.forEach(num => numerosYaUtilizados.add(num))
      }
    })
    
    const numerosGenerados = []
    let intentos = 0
    const maxIntentos = totalBoletas * 2 // Evitar bucle infinito
    
    // Generar números únicos aleatorios que no estén ya utilizados
    while (numerosGenerados.length < cantidadBoletos && intentos < maxIntentos) {
      const numeroAleatorio = Math.floor(Math.random() * totalBoletas) + 1
      
      // Verificar que no esté ya generado y que no esté ya utilizado en otros premios
      if (!numerosGenerados.includes(numeroAleatorio) && !numerosYaUtilizados.has(numeroAleatorio)) {
        numerosGenerados.push(numeroAleatorio)
      }
      intentos++
    }
    
    if (numerosGenerados.length < cantidadBoletos) {
      toast.error(`Solo se pudieron generar ${numerosGenerados.length} números únicos de ${cantidadBoletos} solicitados. Hay muchos números ya utilizados.`)
    }
    
    // Ordenar los números y convertir a string
    const numerosString = numerosGenerados.sort((a, b) => a - b).join(', ')
    
    updatePremioSecundario(premioId, 'numeros_seleccionados', numerosString)
    toast.success(`${numerosGenerados.length} números únicos generados automáticamente`)
  }

  // Modal para ver detalles del usuario
  const ViewUserModal = () => {
    if (!showViewModal || !selectedUser) return null

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-white">Detalles del Usuario</h3>
            <button onClick={closeModals} className="text-gray-400 hover:text-white">
              ✕
            </button>
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-gray-400 text-sm">Nombre:</label>
              <p className="text-white">{selectedUser.name}</p>
            </div>
            <div>
              <label className="text-gray-400 text-sm">Email:</label>
              <p className="text-white">{selectedUser.email}</p>
            </div>
            <div>
              <label className="text-gray-400 text-sm">Teléfono:</label>
              <p className="text-white">{selectedUser.phone}</p>
            </div>
            <div>
              <label className="text-gray-400 text-sm">Tipo de Documento:</label>
              <p className="text-white">{selectedUser.typedoc}</p>
            </div>
            <div>
              <label className="text-gray-400 text-sm">Documento:</label>
              <p className="text-white">{selectedUser.doc}</p>
            </div>
            <div>
              <label className="text-gray-400 text-sm">Ciudad:</label>
              <p className="text-white">{selectedUser.city}</p>
            </div>
            <div>
              <label className="text-gray-400 text-sm">Rol:</label>
              <p className="text-white capitalize">{selectedUser.role}</p>
            </div>
            <div>
              <label className="text-gray-400 text-sm">Fecha de Registro:</label>
              <p className="text-white">{new Date(selectedUser.created_at).toLocaleDateString()}</p>
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <button
              onClick={closeModals}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Modal para editar usuario
  const EditUserModal = () => {
    const [editForm, setEditForm] = useState({
      name: '',
      phone: '',
      typedoc: 'cedula',
      doc: '',
      city: '',
      role: 'client'
    })
    const [editLoading, setEditLoading] = useState(false)

    // Inicializar formulario cuando se abre el modal
    React.useEffect(() => {
      if (selectedUser) {
        setEditForm({
          name: selectedUser.name || '',
          phone: selectedUser.phone || '',
          typedoc: selectedUser.typedoc || 'cedula',
          doc: selectedUser.doc || '',
          city: selectedUser.city || '',
          role: selectedUser.role || 'client'
        })
      }
    }, [selectedUser])

    const handleEditSubmit = async (e) => {
      e.preventDefault()
      setEditLoading(true)
      
      try {
        await userService.updateUser(selectedUser.id, editForm)
        // Recargar lista de usuarios (solo clientes)
        const response = await userService.getAllUsers()
        const clients = (response.users || []).filter(user => user.role === 'client')
        setUsers(clients)
        closeModals()
      } catch (error) {
        console.error('Error al actualizar usuario:', error)
        alert('Error al actualizar usuario')
      } finally {
        setEditLoading(false)
      }
    }

    if (!showEditModal || !selectedUser) return null

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-white">Editar Usuario</h3>
            <button onClick={closeModals} className="text-gray-400 hover:text-white">
              ✕
            </button>
          </div>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-400 text-sm mb-1">Nombre:</label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Teléfono:</label>
              <input
                type="text"
                value={editForm.phone}
                onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Tipo de Documento:</label>
              <select
                value={editForm.typedoc}
                onChange={(e) => setEditForm({...editForm, typedoc: e.target.value})}
                className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                required
              >
                <option value="cedula">Cédula de Ciudadanía</option>
                <option value="cedula_extranjeria">Cédula de Extranjería</option>
                <option value="pasaporte">Pasaporte</option>
                <option value="tarjeta_identidad">Tarjeta de Identidad</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Número de Documento:</label>
              <input
                type="text"
                value={editForm.doc}
                onChange={(e) => setEditForm({...editForm, doc: e.target.value})}
                className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Ciudad:</label>
              <input
                type="text"
                value={editForm.city}
                onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Rol:</label>
              <select
                value={editForm.role}
                onChange={(e) => setEditForm({...editForm, role: e.target.value})}
                className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
              >
                <option value="client">Cliente</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={closeModals}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={editLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {editLoading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // Modal para confirmar eliminación de usuario
  const DeleteUserModal = () => {
    const [deleteLoading, setDeleteLoading] = useState(false)

    const handleDeleteConfirm = async () => {
      setDeleteLoading(true)
      
      try {
        await userService.deleteUser(selectedUser.id)
        // Recargar lista de usuarios (solo clientes)
        const response = await userService.getAllUsers()
        const clients = (response.users || []).filter(user => user.role === 'client')
        setUsers(clients)
        closeModals()
      } catch (error) {
        console.error('Error al eliminar usuario:', error)
        toast.error('Error al eliminar usuario')
      } finally {
        setDeleteLoading(false)
      }
    }

    if (!showDeleteModal || !selectedUser) return null

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-white">Confirmar Eliminación</h3>
            <button onClick={closeModals} className="text-gray-400 hover:text-white">
              ✕
            </button>
          </div>
          <div className="mb-6">
            <p className="text-white mb-2">
              ¿Estás seguro de que deseas eliminar al usuario?
            </p>
            <div className="bg-gray-700 p-3 rounded">
              <p className="text-white font-medium">{selectedUser.name}</p>
              <p className="text-gray-400 text-sm">{selectedUser.email}</p>
            </div>
            <p className="text-red-400 text-sm mt-2">
              Esta acción no se puede deshacer.
            </p>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              onClick={closeModals}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleDeleteConfirm}
              disabled={deleteLoading}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {deleteLoading ? 'Eliminando...' : 'Eliminar'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Modal para editar sorteo
  const EditSorteoModal = () => {
    const [editSorteoForm, setEditSorteoForm] = useState({
      name: '',
      description: '',
      amount: '',
      total_boletas: '',
      start_date: '',
      end_date: '',
      image_url: ''
    })
    const [editSorteoLoading, setEditSorteoLoading] = useState(false)
    const [editImageFile, setEditImageFile] = useState(null)

    // Inicializar formulario cuando se abre el modal
    React.useEffect(() => {
      if (selectedSorteo) {
        setEditSorteoForm({
          name: selectedSorteo.name || '',
          description: selectedSorteo.description || '',
          amount: selectedSorteo.amount || '',
          total_boletas: selectedSorteo.total_boletas || '',
          start_date: selectedSorteo.start_date ? selectedSorteo.start_date.split('T')[0] : '',
          end_date: selectedSorteo.end_date ? selectedSorteo.end_date.split('T')[0] : '',
          image_url: selectedSorteo.image_url || ''
        })
      }
    }, [selectedSorteo])

    const handleEditSorteoSubmit = async (e) => {
      e.preventDefault()
      setEditSorteoLoading(true)
      
      try {
        let imageUrl = editSorteoForm.image_url
        
        // Si hay un archivo de imagen, subirlo primero
        if (editImageFile) {
          try {
            const uploadResponse = await membresiaService.uploadImage(editImageFile)
            imageUrl = uploadResponse.imageUrl
          } catch (uploadError) {
            console.error('Error al subir imagen:', uploadError)
            toast.error('Error al subir la imagen')
            setEditSorteoLoading(false)
            return
          }
        }
        
        const sorteoData = {
          ...editSorteoForm,
          amount: parseFloat(editSorteoForm.amount),
          total_boletas: parseInt(editSorteoForm.total_boletas),
          image_url: imageUrl
        }
        
        await membresiaService.updateMembresia(selectedSorteo.id, sorteoData)
        
        // Recargar lista de sorteos
        const response = await membresiaService.getAllMembresias()
        setSorteos(response || [])
        
        closeSorteoModals()
        alert('Sorteo actualizado exitosamente')
      } catch (error) {
        console.error('Error al actualizar sorteo:', error)
        alert('Error al actualizar sorteo')
      } finally {
        setEditSorteoLoading(false)
      }
    }

    if (!showEditSorteoModal || !selectedSorteo) return null

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-white">Editar Membresía</h3>
            <button onClick={closeSorteoModals} className="text-gray-400 hover:text-white">
              ✕
            </button>
          </div>
          <form onSubmit={handleEditSorteoSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-1">Nombre:</label>
                <input
                  type="text"
                  value={editSorteoForm.name}
                  onChange={(e) => setEditSorteoForm({...editSorteoForm, name: e.target.value})}
                  className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Precio por Boleto:</label>
                <input
                  type="number"
                  value={editSorteoForm.amount}
                  onChange={(e) => setEditSorteoForm({...editSorteoForm, amount: e.target.value})}
                  className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Descripción:</label>
              <textarea
                rows={3}
                value={editSorteoForm.description}
                onChange={(e) => setEditSorteoForm({...editSorteoForm, description: e.target.value})}
                className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                required
              />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-1">Total Boletos:</label>
                <input
                  type="number"
                  value={editSorteoForm.total_boletas}
                  onChange={(e) => setEditSorteoForm({...editSorteoForm, total_boletas: e.target.value})}
                  className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Fecha Inicio:</label>
                <input
                  type="date"
                  value={editSorteoForm.start_date}
                  onChange={(e) => setEditSorteoForm({...editSorteoForm, start_date: e.target.value})}
                  className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Fecha Fin:</label>
                <input
                  type="date"
                  value={editSorteoForm.end_date}
                  onChange={(e) => setEditSorteoForm({...editSorteoForm, end_date: e.target.value})}
                  className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Imagen Principal:</label>
              <div className="space-y-3">
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.gif,.webp,image/png,image/jpeg,image/gif,image/webp"
                  onChange={(e) => setEditImageFile(e.target.files[0])}
                  className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600"
                />
                <p className="text-gray-400 text-sm">O ingresa una URL de imagen:</p>
                <input
                  type="url"
                  value={editSorteoForm.image_url}
                  onChange={(e) => setEditSorteoForm({...editSorteoForm, image_url: e.target.value})}
                  className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                  placeholder="https://ejemplo.com/imagen.jpg"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={closeSorteoModals}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={editSorteoLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {editSorteoLoading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // Modal para confirmar eliminación de sorteo
  const DeleteSorteoModal = () => {
    const [deleteSorteoLoading, setDeleteSorteoLoading] = useState(false)

    const handleDeleteSorteoConfirm = async () => {
    setDeleteSorteoLoading(true)
    
    try {
      await membresiaService.deleteMembresia(selectedSorteo.id)
      
      // Actualizar lista de sorteos inmediatamente removiendo el sorteo eliminado
      setSorteos(prevSorteos => prevSorteos.filter(sorteo => sorteo.id !== selectedSorteo.id))
      
      closeSorteoModals()
      // Eliminado: alert('Sorteo eliminado exitosamente') - sin alert del navegador
    } catch (error) {
      console.error('Error al eliminar sorteo:', error)
      // Recargar lista completa solo en caso de error
      const response = await membresiaService.getAllMembresias()
      setSorteos(response || [])
    } finally {
      setDeleteSorteoLoading(false)
    }
  }

    if (!showDeleteSorteoModal || !selectedSorteo) return null

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-white">Confirmar Eliminación</h3>
            <button onClick={closeSorteoModals} className="text-gray-400 hover:text-white">
              ✕
            </button>
          </div>
          <div className="mb-6">
            <p className="text-white mb-2">
              ¿Estás seguro de que deseas eliminar esta membresía?
            </p>
            <div className="bg-gray-700 p-3 rounded">
              <p className="text-white font-medium">{selectedSorteo.name}</p>
              <p className="text-gray-400 text-sm">ID: #{selectedSorteo.id}</p>
            </div>
            <p className="text-red-400 text-sm mt-2">
              Esta acción no se puede deshacer y eliminará todos los datos relacionados.
            </p>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              onClick={closeSorteoModals}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleDeleteSorteoConfirm}
              disabled={deleteSorteoLoading}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {deleteSorteoLoading ? 'Eliminando...' : 'Eliminar'}
            </button>
          </div>
        </div>
      </div>
    )
  }



  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: Settings },
    { id: 'users', name: 'Clientes', icon: Users },
    { id: 'raffles', name: 'Membresías', icon: Gift }
  ]



  const renderDashboard = () => (
    <div className="space-y-8">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-300 text-sm">Total Usuarios</p>
              {loading ? (
                <div className="animate-pulse bg-white/20 h-8 w-16 rounded mt-1"></div>
              ) : (
                <p className="text-3xl font-bold text-white">{stats.totalUsers.toLocaleString()}</p>
              )}
            </div>
            <Users className="h-12 w-12 text-blue-400" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-300 text-sm">Membresías Activas</p>
              {loading ? (
                <div className="animate-pulse bg-white/20 h-8 w-12 rounded mt-1"></div>
              ) : (
                <p className="text-3xl font-bold text-white">{stats.activeRaffles}</p>
              )}
            </div>
            <Gift className="h-12 w-12 text-green-400" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-300 text-sm">Ingresos Totales</p>
              {loading ? (
                <div className="animate-pulse bg-white/20 h-8 w-24 rounded mt-1"></div>
              ) : (
                <p className="text-3xl font-bold text-white">${stats.totalRevenue.toLocaleString()}</p>
              )}
            </div>
            <DollarSign className="h-12 w-12 text-yellow-400" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-300 text-sm">Boletos Vendidos</p>
              {loading ? (
                <div className="animate-pulse bg-white/20 h-8 w-20 rounded mt-1"></div>
              ) : (
                <p className="text-3xl font-bold text-white">{stats.ticketsSold.toLocaleString()}</p>
              )}
            </div>
            <Trophy className="h-12 w-12 text-purple-400" />
          </div>
        </motion.div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
        <h3 className="text-xl font-semibold text-white mb-4">Actividad Reciente</h3>
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
              <p className="text-gray-400 mt-2">Cargando actividad...</p>
            </div>
          ) : recentActivity.length > 0 ? (
            recentActivity.map((activity, index) => (
              <div key={activity.id} className={`flex items-center justify-between py-3 ${index < recentActivity.length - 1 ? 'border-b border-white/10' : ''}`}>
                <div>
                  <p className="text-white font-medium">{activity.tipo}</p>
                  <p className="text-gray-400 text-sm">{activity.descripcion}</p>
                </div>
                <span className="text-gray-400 text-sm">
                  {new Date(activity.created_at).toLocaleDateString('es-ES', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-400">No hay actividad reciente</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  const renderUsers = () => (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar usuarios..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <button className="flex items-center space-x-2 bg-white/10 border border-white/20 text-white px-4 py-3 rounded-lg hover:bg-white/20 transition-colors">
          <Filter className="h-5 w-5" />
          <span>Filtros</span>
        </button>
      </div>

      {/* Users Table */}
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left p-4 text-gray-300 font-medium">Usuario</th>
                <th className="text-left p-4 text-gray-300 font-medium">Contacto</th>
                <th className="text-left p-4 text-gray-300 font-medium">Ciudad</th>
                <th className="text-left p-4 text-gray-300 font-medium">Boletos</th>
                <th className="text-left p-4 text-gray-300 font-medium">Gastado</th>
                <th className="text-left p-4 text-gray-300 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {usersLoading ? (
                <tr>
                  <td colSpan="6" className="p-8 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                      <span className="text-gray-400">Cargando usuarios...</span>
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-8 text-center text-gray-400">
                    No hay usuarios registrados
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="border-t border-white/10 hover:bg-white/5 transition-colors">
                    <td className="p-4">
                      <div>
                        <p className="text-white font-medium">{user.name}</p>
                        <p className="text-gray-400 text-sm">Registro: {new Date(user.created_at).toLocaleDateString()}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <div>
                        <p className="text-white">{user.email}</p>
                        <p className="text-gray-400 text-sm">{user.phone}</p>
                      </div>
                    </td>
                    <td className="p-4 text-white">{user.city}</td>
                    <td className="p-4 text-white">0</td>
                    <td className="p-4 text-white">$0</td>
                    <td className="p-4">
                       <div className="flex space-x-2">
                         <button 
                           onClick={() => handleViewUser(user)}
                           className="text-blue-400 hover:text-blue-300 transition-colors"
                           title="Ver detalles"
                         >
                           <Eye className="h-4 w-4" />
                         </button>
                         <button 
                           onClick={() => handleEditUser(user)}
                           className="text-yellow-400 hover:text-yellow-300 transition-colors"
                           title="Editar usuario"
                         >
                           <Edit className="h-4 w-4" />
                         </button>
                         <button 
                           onClick={() => handleDeleteUser(user)}
                           className="text-red-400 hover:text-red-300 transition-colors"
                           title="Eliminar usuario"
                         >
                           <Trash2 className="h-4 w-4" />
                         </button>
                       </div>
                     </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  const renderRaffles = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Gestión de Membresías</h2>
        <button 
          onClick={() => setActiveTab('create-raffle')}
          className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-primary-600 hover:to-secondary-600 transition-all flex items-center space-x-2"
        >
          <Plus className="h-5 w-5" />
          <span>Nueva Membresía</span>
        </button>
      </div>

      <div className="grid gap-6">
        {sorteosLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
            <p className="text-gray-400 mt-2">Cargando membresías...</p>
          </div>
        ) : sorteos.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400">No hay membresías disponibles</p>
          </div>
        ) : (
          sorteos.map((raffle) => (
          <motion.div
            key={raffle.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
          >
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="w-full lg:w-48 h-32 rounded-lg overflow-hidden relative">
                {raffle.image_url ? (
                  <img 
                    src={raffle.image_url} 
                    alt={raffle.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={`w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center ${raffle.image_url ? 'absolute inset-0' : ''} ${raffle.image_url ? 'hidden' : ''}`}>
                  <span className="text-white font-bold text-lg">#{raffle.id}</span>
                </div>
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">{raffle.name}</h3>
                    <p className="text-gray-300">Membresía con 100,000 boletas disponibles</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    raffle.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                  }`}>
                    {raffle.status === 'active' ? 'Activo' : raffle.status === 'completed' ? 'Finalizado' : 'Inactivo'}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div>
                    <p className="text-gray-400 text-sm">Precio por boleto</p>
                    <p className="text-white font-semibold">${raffle.amount?.toLocaleString() || 1000}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Vendidos</p>
                    <p className="text-white font-semibold">{raffle.boletas_vendidas?.toLocaleString() || 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Total boletos</p>
                    <p className="text-white font-semibold">{raffle.total_boletas?.toLocaleString() || 100000}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Progreso</p>
                    <p className="text-white font-semibold">{Math.round(((raffle.boletas_vendidas || 0) / (raffle.total_boletas || 100000)) * 100)}%</p>
                  </div>
                </div>
                
                <div className="flex space-x-3">
                  <button 
                    onClick={() => window.open(`/membresia/${raffle.id}`, '_blank')}
                    className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    <Eye className="h-4 w-4" />
                    <span>Ver</span>
                  </button>
                  <button 
                    onClick={() => handleViewPurchasedTickets(raffle)}
                    className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    <Ticket className="h-4 w-4" />
                    <span>Ver Boletos Comprados</span>
                  </button>
                  <button 
                    onClick={() => handleEditSorteo(raffle)}
                    className="flex items-center space-x-2 bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Editar</span>
                  </button>
                  <button 
                    onClick={() => handleDeleteSorteo(raffle)}
                    className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>Eliminar</span>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        ))
        )}
      </div>
    </div>
  )

  const renderCreateRaffle = () => {
    const steps = [
      { number: 1, title: 'Datos de la Membresía', description: 'Información básica' },
      { number: 2, title: 'Premios Secundarios', description: 'Configurar premios' },
      { number: 3, title: 'Resumen', description: 'Revisar y finalizar' }
    ]

    return (
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold text-white mb-8">Crear Nueva Membresía</h2>
        
        {/* Navegación de pasos */}
        <div className="flex items-center justify-center mb-8">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                createStep >= step.number 
                  ? 'bg-primary-500 border-primary-500 text-white' 
                  : 'border-gray-400 text-gray-400'
              }`}>
                {step.number}
              </div>
              <div className="ml-3 mr-8">
                <p className={`text-sm font-medium ${
                  createStep >= step.number ? 'text-white' : 'text-gray-400'
                }`}>
                  {step.title}
                </p>
                <p className="text-xs text-gray-500">{step.description}</p>
              </div>
              {index < steps.length - 1 && (
                <div className={`w-16 h-0.5 ${
                  createStep > step.number ? 'bg-primary-500' : 'bg-gray-400'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Contenido del paso actual */}
        {createStep === 1 && renderStep1()}
        {createStep === 2 && renderStep2()}
        {createStep === 3 && renderStep3()}
      </div>
    )
  }

  // Paso 1: Datos del Sorteo
  const renderStep1 = () => (
    <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
      <h3 className="text-xl font-bold text-white mb-6">Datos Básicos de la Membresía</h3>
      
      <form onSubmit={handleCreateSorteoBasico} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Título de la Membresía</label>
            <input
              type="text"
              value={createSorteoForm.name}
              onChange={(e) => setCreateSorteoForm({...createSorteoForm, name: e.target.value})}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Ej: iPhone 15 Pro Max"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Precio por Boleto (COP)</label>
            <input
              type="number"
              value={createSorteoForm.amount}
              onChange={(e) => setCreateSorteoForm({...createSorteoForm, amount: e.target.value})}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="1000"
              required
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Descripción</label>
          <textarea
            rows={4}
            value={createSorteoForm.description}
            onChange={(e) => setCreateSorteoForm({...createSorteoForm, description: e.target.value})}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Describe el premio y sus características..."
            required
          />
        </div>
        
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Total de Boletos</label>
            <input
              type="number"
              value={createSorteoForm.total_boletas}
              onChange={(e) => setCreateSorteoForm({...createSorteoForm, total_boletas: e.target.value})}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="50000"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Fecha de Inicio</label>
            <input
              type="date"
              value={createSorteoForm.start_date}
              onChange={(e) => setCreateSorteoForm({...createSorteoForm, start_date: e.target.value})}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Fecha de Fin</label>
            <input
              type="date"
              value={createSorteoForm.end_date}
              onChange={(e) => setCreateSorteoForm({...createSorteoForm, end_date: e.target.value})}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Imagen Principal</label>
          <div className="space-y-3">
            <input
              type="file"
              accept=".png,.jpg,.jpeg,.gif,.webp,image/png,image/jpeg,image/gif,image/webp"
              onChange={(e) => setImageFile(e.target.files[0])}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-500 file:text-white hover:file:bg-primary-600"
            />
            <p className="text-gray-400 text-sm">O ingresa una URL de imagen:</p>
            <input
              type="url"
              value={createSorteoForm.image_url}
              onChange={(e) => setCreateSorteoForm({...createSorteoForm, image_url: e.target.value})}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="https://ejemplo.com/imagen.jpg"
            />
          </div>
        </div>
        
        <div className="flex space-x-4">
          <button
            type="submit"
            disabled={createLoading}
            className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-8 py-3 rounded-lg font-semibold hover:from-primary-600 hover:to-secondary-600 transition-all disabled:opacity-50"
          >
            {createLoading ? 'Creando...' : 'Crear Membresía y Continuar'}
          </button>
          <button
            type="button"
            onClick={() => {
              resetCreateForm()
              setActiveTab('raffles')
            }}
            className="bg-gray-600 hover:bg-gray-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  )

  // Paso 2: Premios Secundarios
  const renderStep2 = () => (
    <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-bold text-white">Premios Secundarios</h3>
          <p className="text-gray-400 text-sm mt-1">Membresía ID: {createdSorteoId} | Total de boletos: {createSorteoForm.total_boletas}</p>
        </div>
        <button
          onClick={addPremioSecundario}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm transition-colors flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Agregar Premio</span>
        </button>
      </div>
      
      <div className="space-y-4">
        {premiosSecundarios.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <Gift className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No hay premios secundarios agregados</p>
            <p className="text-sm">Haz clic en "Agregar Premio" para comenzar</p>
          </div>
        ) : (
          premiosSecundarios.map((premio) => (
            <div key={premio.id} className="bg-white/5 rounded-lg p-6 border border-white/10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Número de Boletos</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={premio.numero_boletos}
                      onChange={(e) => updatePremioSecundario(premio.id, 'numero_boletos', e.target.value)}
                      className="flex-1 px-3 py-2 bg-white/10 border border-white/20 rounded text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      placeholder="Ej: 5"
                    />
                    <button
                      type="button"
                      onClick={() => generarNumerosAutomaticos(premio.id)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm transition-colors whitespace-nowrap"
                      title="Generar números automáticamente"
                    >
                      Generar
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Premio</label>
                  <input
                    type="text"
                    value={premio.premio}
                    onChange={(e) => updatePremioSecundario(premio.id, 'premio', e.target.value)}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    placeholder="Ej: $100,000 COP"
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm text-gray-300 mb-2">Números Seleccionados</label>
                <textarea
                  rows={2}
                  value={premio.numeros_seleccionados}
                  onChange={(e) => updatePremioSecundario(premio.id, 'numeros_seleccionados', e.target.value)}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="Ej: 1234, 5678, 9012, 3456, 7890"
                />
                <p className="text-xs text-gray-500 mt-1">Números separados por comas</p>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="flex space-x-2">
                  <button
                    onClick={() => guardarPremioSecundario(premio)}
                    disabled={premio.guardado}
                    className={`px-4 py-2 rounded text-sm transition-colors ${
                      premio.guardado 
                        ? 'bg-green-600 text-white cursor-not-allowed' 
                        : 'bg-primary-600 hover:bg-primary-700 text-white'
                    }`}
                  >
                    {premio.guardado ? '✓ Guardado' : 'Guardar Premio'}
                  </button>
                </div>
                <button
                  onClick={() => removePremioSecundario(premio.id)}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm transition-colors flex items-center space-x-2"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Eliminar</span>
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      
      <div className="flex justify-between mt-8">
        <button
          onClick={() => setCreateStep(1)}
          className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
        >
          ← Volver
        </button>
        <button
          onClick={() => setCreateStep(3)}
          className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
        >
          Continuar al Resumen →
        </button>
      </div>
    </div>
  )

  // Paso 3: Resumen
  const renderStep3 = () => (
    <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
      <h3 className="text-xl font-bold text-white mb-6">Resumen de la Membresía</h3>
      
      <div className="space-y-6">
        {/* Datos del sorteo */}
        <div className="bg-white/5 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-white mb-4">Información de la Membresía</h4>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div><span className="text-gray-400">Título:</span> <span className="text-white">{createSorteoForm.name}</span></div>
            <div><span className="text-gray-400">Precio por boleto:</span> <span className="text-white">${createSorteoForm.amount} COP</span></div>
            <div><span className="text-gray-400">Total de boletos:</span> <span className="text-white">{createSorteoForm.total_boletas}</span></div>
            <div><span className="text-gray-400">ID de la membresía:</span> <span className="text-white">{createdSorteoId}</span></div>
            <div className="md:col-span-2"><span className="text-gray-400">Descripción:</span> <span className="text-white">{createSorteoForm.description}</span></div>
          </div>
        </div>
        
        {/* Premios secundarios */}
        <div className="bg-white/5 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-white mb-4">Premios Secundarios ({premiosSecundarios.length})</h4>
          {premiosSecundarios.length === 0 ? (
            <p className="text-gray-400">No se han agregado premios secundarios</p>
          ) : (
            <div className="space-y-3">
              {premiosSecundarios.map((premio, index) => (
                <div key={premio.id} className="bg-white/5 rounded p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-white font-medium">Premio {index + 1}: {premio.premio}</p>
                      <p className="text-gray-400 text-sm">{premio.numero_boletos} boletos ganadores</p>
                      <p className="text-gray-400 text-xs mt-1">Números: {premio.numeros_seleccionados}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${
                      premio.guardado ? 'bg-green-600 text-white' : 'bg-yellow-600 text-white'
                    }`}>
                      {premio.guardado ? 'Guardado' : 'Pendiente'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <div className="flex justify-between mt-8">
        <button
          onClick={() => setCreateStep(2)}
          className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
        >
          ← Volver a Premios
        </button>
        <button
          onClick={handleFinalizarSorteo}
          className="bg-gradient-to-r from-green-500 to-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-green-600 hover:to-green-700 transition-all"
        >
          ✓ Finalizar Membresía
        </button>
      </div>
    </div>
  )

  const renderPurchasedTickets = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Boletos por Membresía</h2>
          <p className="text-gray-400 mt-1">
            Membresía: {selectedSorteoForTickets?.name} (ID: {selectedSorteoForTickets?.id})
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select 
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all" className="bg-gray-800">Todos los Estados</option>
              <option value="pending" className="bg-gray-800">Pendientes</option>
              <option value="paid" className="bg-gray-800">Completadas</option>
            </select>
          </div>
          <button 
            onClick={closePurchasedTicketsView}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center space-x-2"
          >
            <span>← Volver a Membresías</span>
          </button>
        </div>
      </div>

      {purchasedTicketsLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
          <p className="text-gray-400 mt-2">Cargando boletos comprados...</p>
        </div>
      ) : purchasedTicketsData.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-400">No hay boletos comprados para esta membresía</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-white">{purchasedTicketsData.reduce((total, user) => total + user.tickets.length, 0)}</p>
                <p className="text-gray-400">Total Boletos Vendidos</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{purchasedTicketsData.length}</p>
                <p className="text-gray-400">Usuarios Participantes</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  ${(purchasedTicketsData.reduce((total, user) => total + user.tickets.length, 0) * (selectedSorteoForTickets?.amount || 0)).toLocaleString()} COP
                </p>
                <p className="text-gray-400">Ingresos Generados</p>
              </div>
            </div>
          </div>

          {purchasedTicketsData.map((userGroup, index) => (
            <motion.div
              key={userGroup.user.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold text-white">{userGroup.user.name}</h3>
                  <div className="text-gray-300 text-sm space-y-1">
                    <p>Email: {userGroup.user.email}</p>
                    <p>Teléfono: {userGroup.user.phone}</p>
                    <p>Documento: {userGroup.user.doc}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-white">{userGroup.tickets.length} boleto{userGroup.tickets.length > 1 ? 's' : ''}</p>
                  <p className="text-gray-400 text-sm">
                    ${(userGroup.tickets.length * (selectedSorteoForTickets?.amount || 0)).toLocaleString()} COP
                  </p>
                  <div className="flex items-center space-x-2 mt-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      ['paid', 'completed', 'approved'].includes(userGroup.tickets[0]?.status)
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                        : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                    }`}>
                      {['paid', 'completed', 'approved'].includes(userGroup.tickets[0]?.status) ? 'Completada' : 'Pendiente'}
                    </span>
                    {userGroup.tickets[0]?.status === 'pending' && (
                      <div className="flex space-x-1">
                        <button
                          onClick={() => handleApproveTransaction(userGroup.tickets[0].reference)}
                          disabled={actionLoading}
                          className="bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white p-1 rounded transition-colors"
                          title="Aprobar transacción"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTransaction(userGroup.tickets[0].reference)}
                          disabled={actionLoading}
                          className="bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white p-1 rounded transition-colors"
                          title="Eliminar transacción"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-8 gap-3">
                {userGroup.tickets.map((ticket, ticketIndex) => (
                  <div
                    key={ticketIndex}
                    className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white text-center py-2 px-3 rounded-lg font-mono font-bold"
                    title={`Boleto #${ticket.numero} - Referencia: ${ticket.reference}`}
                  >
                    {ticket.numero}
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  )

  const renderTabContent = () => {
    if (showPurchasedTickets) {
      return renderPurchasedTickets()
    }
    
    switch (activeTab) {
      case 'dashboard':
        return renderDashboard()
      case 'users':
        return renderUsers()
      case 'raffles':
        return renderRaffles()
      case 'create-raffle':
        return renderCreateRaffle()
      default:
        return renderDashboard()
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      <Header />
      
      <div className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Panel de Administración</h1>
            <p className="text-gray-300">Gestiona usuarios, sorteos y configuraciones</p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8">
            {/* Sidebar with Tabs */}
            <div className="lg:col-span-1">
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
                <div className="space-y-2">
                  {tabs.map((tab) => {
                    const Icon = tab.icon
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                          activeTab === tab.id
                            ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white'
                            : 'text-gray-300 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        <Icon className="h-5 w-5" />
                        <span className="font-medium">{tab.name}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-4">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
      
      {/* Modales */}
      <ViewUserModal />
      <EditUserModal />
      <DeleteUserModal />
      <EditSorteoModal />
      <DeleteSorteoModal />
      
      {/* Modal de confirmación para aprobar transacción */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">Confirmar Aprobación</h3>
              <button 
                onClick={() => setShowApproveModal(false)} 
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            <div className="mb-6">
              <p className="text-white mb-2">
                ¿Estás seguro de que deseas aprobar esta transacción?
              </p>
              <div className="bg-gray-700 p-3 rounded">
                <p className="text-white font-medium">Referencia: {selectedTransaction}</p>
              </div>
              <p className="text-green-400 text-sm mt-2">
                Los números de boletos quedarán bloqueados y la transacción se marcará como completada.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowApproveModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmApproveTransaction}
                disabled={actionLoading}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Aprobando...' : 'Aprobar'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de confirmación para eliminar transacción */}
      {showDeleteTransactionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">Confirmar Eliminación</h3>
              <button 
                onClick={() => setShowDeleteTransactionModal(false)} 
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            <div className="mb-6">
              <p className="text-white mb-2">
                ¿Estás seguro de que deseas eliminar esta transacción?
              </p>
              <div className="bg-gray-700 p-3 rounded">
                <p className="text-white font-medium">Referencia: {selectedTransaction}</p>
              </div>
              <p className="text-red-400 text-sm mt-2">
                Esta acción eliminará la transacción y liberará los boletos para que otros usuarios puedan comprarlos. Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteTransactionModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDeleteTransaction}
                disabled={actionLoading}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Toast Notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#1f2937',
            color: '#fff',
            border: '1px solid #374151'
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff'
            }
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff'
            }
          }
        }}
      />
    </div>
  )
}

export default AdminPage